export const costPostures = ['minimal', 'lean', 'evidence', 'prod'] as const;

export type CostPosture = (typeof costPostures)[number];

export const EksControlPlaneLogTypes = [
  'api',
  'audit',
  'authenticator',
  'controllerManager',
  'scheduler',
] as const;
export type EksControlPlaneLogType = (typeof EksControlPlaneLogTypes)[number];

export const BudgetEnforcements = ['fail', 'mandatory', 'approval'] as const;
export type BudgetEnforcement = (typeof BudgetEnforcements)[number];

export interface KarpenterCpuLimits {
  readonly system: string;
  readonly stateful: string;
  readonly app: string;
}

export interface KarpenterMemoryLimits {
  readonly system: string;
  readonly stateful: string;
  readonly app: string;
}

export interface PlatformSizing {
  readonly availabilityZoneCount: number;
  readonly systemNode: {
    readonly minSize: number;
    readonly desiredSize: number;
    readonly maxSize: number;
  };
  readonly appPoolStrategy:
    | 'spot-graviton-limited'
    | 'spot-first'
    | 'evidence'
    | 'spot-first-fallback'
    | 'reserved-ha';
  readonly statefulNodePoolEnabled: boolean;
  readonly karpenterCpuLimits: KarpenterCpuLimits;
  readonly karpenterMemoryLimits: KarpenterMemoryLimits;
  readonly natGatewayStrategy: 'None' | 'Single' | 'OnePerAz';
  readonly controlPlaneLogTypes: readonly EksControlPlaneLogType[];
  readonly logRetentionDays: number;
  readonly publicLoadBalancerLimit: number | null;
  readonly budgetEnforcement: BudgetEnforcement;
  readonly preventDestroy: boolean;
  // Arms the off-hours control-plane stop. A cost-optimised posture whose nodes still ran 24/7
  // is the failure this closes. Non-prod arms it; evidence/prod never do (FAPI).
  readonly offHoursStopDefault: boolean;
}

export interface RuntimeSizing {
  readonly serviceReplicas: number;
  readonly publicApiReplicas: number;
  readonly postgresInstances: number;
  readonly redisClusterSize: number;
  readonly keycloakReplicas: number;
  readonly kongReplicas: number;
  readonly natsReplicas: number;
  readonly kyvernoAdmissionReplicas: number;
  readonly kyvernoControllerReplicas: number;
  readonly multiNode: boolean;
  readonly ingressHighlyAvailable: boolean;
  readonly podDisruptionBudget: {
    readonly enabled: boolean;
    readonly minAvailable: number;
  };
  readonly securityEvidenceControls: 'dev-relaxed' | 'full';
  // Arms KEDA off-hours scale-to-zero. Paired with `offHoursStopDefault` so the workload floor
  // and the control-plane stop share one decision (the runner and program used to drift).
  readonly scaleToZeroDefault: boolean;
}

const FULL_EKS_LOG_TYPES = [
  'api',
  'audit',
  'authenticator',
  'controllerManager',
  'scheduler',
] as const satisfies readonly EksControlPlaneLogType[];

const LOW_COST_EKS_LOG_TYPES = [
  'api',
  'authenticator',
] as const satisfies readonly EksControlPlaneLogType[];

const LOW_COST_CPU_LIMITS = {
  system: '16',
  stateful: '16',
  app: '32',
} as const satisfies KarpenterCpuLimits;

const LOW_COST_MEMORY_LIMITS = {
  system: '32Gi',
  stateful: '32Gi',
  app: '64Gi',
} as const satisfies KarpenterMemoryLimits;

const PRODUCTION_CPU_LIMITS = {
  system: '32',
  stateful: '64',
  app: '128',
} as const satisfies KarpenterCpuLimits;

const PRODUCTION_MEMORY_LIMITS = {
  system: '64Gi',
  stateful: '128Gi',
  app: '256Gi',
} as const satisfies KarpenterMemoryLimits;

const PRODUCTION_PLATFORM_BASE = {
  availabilityZoneCount: 3,
  systemNode: { minSize: 2, desiredSize: 2, maxSize: 4 },
  statefulNodePoolEnabled: true,
  karpenterCpuLimits: PRODUCTION_CPU_LIMITS,
  karpenterMemoryLimits: PRODUCTION_MEMORY_LIMITS,
  natGatewayStrategy: 'OnePerAz',
  controlPlaneLogTypes: FULL_EKS_LOG_TYPES,
  logRetentionDays: 30,
  publicLoadBalancerLimit: null,
  budgetEnforcement: 'mandatory',
  preventDestroy: true,
  offHoursStopDefault: false,
} as const;

const PLATFORM_SIZING = {
  minimal: {
    availabilityZoneCount: 1,
    systemNode: { minSize: 1, desiredSize: 1, maxSize: 2 },
    appPoolStrategy: 'spot-graviton-limited',
    statefulNodePoolEnabled: false,
    karpenterCpuLimits: LOW_COST_CPU_LIMITS,
    karpenterMemoryLimits: LOW_COST_MEMORY_LIMITS,
    natGatewayStrategy: 'Single',
    controlPlaneLogTypes: LOW_COST_EKS_LOG_TYPES,
    logRetentionDays: 7,
    publicLoadBalancerLimit: 1,
    budgetEnforcement: 'fail',
    preventDestroy: false,
    offHoursStopDefault: true,
  },
  lean: {
    availabilityZoneCount: 2,
    systemNode: { minSize: 1, desiredSize: 1, maxSize: 3 },
    appPoolStrategy: 'spot-first',
    statefulNodePoolEnabled: false,
    karpenterCpuLimits: LOW_COST_CPU_LIMITS,
    karpenterMemoryLimits: LOW_COST_MEMORY_LIMITS,
    natGatewayStrategy: 'Single',
    controlPlaneLogTypes: LOW_COST_EKS_LOG_TYPES,
    logRetentionDays: 14,
    publicLoadBalancerLimit: 1,
    budgetEnforcement: 'mandatory',
    preventDestroy: true,
    offHoursStopDefault: true,
  },
  evidence: {
    ...PRODUCTION_PLATFORM_BASE,
    appPoolStrategy: 'evidence',
    budgetEnforcement: 'approval',
  },
  prod: {
    ...PRODUCTION_PLATFORM_BASE,
    appPoolStrategy: 'reserved-ha',
  },
} as const satisfies Record<CostPosture, PlatformSizing>;

const DISABLED_POD_DISRUPTION_BUDGET = { enabled: false, minAvailable: 0 } as const;
const HA_POD_DISRUPTION_BUDGET = { enabled: true, minAvailable: 2 } as const;

const LOW_COST_RUNTIME_BASE = {
  postgresInstances: 1,
  redisClusterSize: 1,
  keycloakReplicas: 1,
  kongReplicas: 1,
  natsReplicas: 1,
  kyvernoControllerReplicas: 1,
  multiNode: false,
  ingressHighlyAvailable: false,
  podDisruptionBudget: DISABLED_POD_DISRUPTION_BUDGET,
  securityEvidenceControls: 'full',
  scaleToZeroDefault: true,
} as const;

const PRODUCTION_RUNTIME_BASE = {
  postgresInstances: 3,
  redisClusterSize: 3,
  keycloakReplicas: 2,
  kongReplicas: 2,
  natsReplicas: 3,
  kyvernoControllerReplicas: 2,
  multiNode: true,
  ingressHighlyAvailable: true,
  podDisruptionBudget: HA_POD_DISRUPTION_BUDGET,
  securityEvidenceControls: 'full',
  scaleToZeroDefault: false,
} as const;

const RUNTIME_SIZING = {
  minimal: {
    ...LOW_COST_RUNTIME_BASE,
    serviceReplicas: 1,
    publicApiReplicas: 1,
    kyvernoAdmissionReplicas: 1,
    securityEvidenceControls: 'dev-relaxed',
  },
  lean: {
    ...LOW_COST_RUNTIME_BASE,
    serviceReplicas: 1,
    publicApiReplicas: 1,
    kyvernoAdmissionReplicas: 1,
  },
  evidence: {
    ...PRODUCTION_RUNTIME_BASE,
    serviceReplicas: 2,
    publicApiReplicas: 2,
    kyvernoAdmissionReplicas: 2,
  },
  prod: {
    ...PRODUCTION_RUNTIME_BASE,
    serviceReplicas: 3,
    publicApiReplicas: 3,
    kyvernoAdmissionReplicas: 3,
  },
} as const satisfies Record<CostPosture, RuntimeSizing>;

export function resolvePlatformSizing(posture: CostPosture): PlatformSizing {
  const sizing = PLATFORM_SIZING[posture];
  return {
    ...sizing,
    systemNode: { ...sizing.systemNode },
    karpenterCpuLimits: { ...sizing.karpenterCpuLimits },
    karpenterMemoryLimits: { ...sizing.karpenterMemoryLimits },
    controlPlaneLogTypes: [...sizing.controlPlaneLogTypes],
  };
}

export function resolveRuntimeSizing(posture: CostPosture): RuntimeSizing {
  const sizing = RUNTIME_SIZING[posture];
  return {
    ...sizing,
    podDisruptionBudget: { ...sizing.podDisruptionBudget },
  };
}
